import { useEffect, useState } from 'react';
import {
  executiveDashboard,
  type ExecutiveDashboardData,
  type StaffRecord,
  type MDAInfo,
} from '@/services/analytics';
import styles from './ExecutiveDashboardPage.module.css';

// Mock data generator for demonstration
function generateMockStaffRecords(): StaffRecord[] {
  const grades = ['Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12', 'Grade 13', 'Grade 14', 'Grade 15', 'Grade 16'];
  const mdas = ['OHCS', 'MOF', 'MOH', 'MOE', 'MOTI', 'MLGRD'];
  const statuses: StaffRecord['status'][] = ['active', 'on_leave', 'seconded', 'suspended'];

  const records: StaffRecord[] = [];

  for (let i = 0; i < 250; i++) {
    const birthYear = 1960 + Math.floor(Math.random() * 35);
    const appointmentYear = Math.max(birthYear + 22, 1995 + Math.floor(Math.random() * 25));

    records.push({
      staffId: `staff_${i + 1}`,
      name: `Staff Member ${i + 1}`,
      gender: Math.random() > 0.45 ? 'male' : 'female',
      dateOfBirth: `${birthYear}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
      appointmentDate: `${appointmentYear}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
      currentGrade: grades[Math.floor(Math.random() * grades.length)],
      currentMDA: mdas[Math.floor(Math.random() * mdas.length)],
      status: Math.random() > 0.1 ? 'active' : statuses[Math.floor(Math.random() * statuses.length)],
      isDigitized: Math.random() > 0.3,
      completenessScore: Math.floor(Math.random() * 40) + 60,
      hasCriticalDocs: Math.random() > 0.2,
      documentCount: Math.floor(Math.random() * 50) + 10,
      lastUpdated: `2024-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
      timeInCurrentGrade: Math.floor(Math.random() * 8) + 1,
    });
  }

  return records;
}

function generateMockMDAs(): MDAInfo[] {
  return [
    { code: 'OHCS', name: 'Office of the Head of Civil Service' },
    { code: 'MOF', name: 'Ministry of Finance' },
    { code: 'MOH', name: 'Ministry of Health' },
    { code: 'MOE', name: 'Ministry of Education' },
    { code: 'MOTI', name: 'Ministry of Trade and Industry' },
    { code: 'MLGRD', name: 'Ministry of Local Government' },
  ];
}

export function ExecutiveDashboardPage() {
  const [dashboardData, setDashboardData] = useState<ExecutiveDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'overview' | 'workforce' | 'retirement' | 'records' | 'alerts'>('overview');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Generate mock data and use the executive dashboard service
        const staffRecords = generateMockStaffRecords();
        const mdaList = generateMockMDAs();

        const data = executiveDashboard.generateDashboard({
          staffRecords,
          mdaList,
          startDate: new Date(2024, 0, 1),
          endDate: new Date(2024, 11, 31),
        });

        setDashboardData(data);
      } catch (err) {
        console.error('Failed to fetch executive dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Executive Dashboard...</span>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>Failed to load dashboard data</div>
      </div>
    );
  }

  const { workforceOverview, recordsHealth, retirementAnalysis, diversityMetrics, gradeDistribution, mdaComparison, alerts } = dashboardData;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>Executive View</div>
          <h1 className={styles.title}>Workforce Intelligence Dashboard</h1>
          <p className={styles.subtitle}>
            Real-time analytics and insights for civil service workforce management
          </p>
        </div>
        <div className={styles.headerMeta}>
          <span className={styles.lastUpdated}>
            Last updated: {dashboardData.generatedAt.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        {[
          { id: 'overview', label: 'Overview', icon: '📊' },
          { id: 'workforce', label: 'Workforce', icon: '👥' },
          { id: 'retirement', label: 'Retirement', icon: '📅' },
          { id: 'records', label: 'Records Health', icon: '📁' },
          { id: 'alerts', label: 'Alerts', icon: '🔔', count: alerts.length },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeSection === tab.id ? styles.active : ''}`}
            onClick={() => setActiveSection(tab.id as typeof activeSection)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.count !== undefined && tab.count > 0 && (
              <span className={styles.tabBadge}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className={styles.content}>
          {/* Key Metrics */}
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <div className={styles.metricIcon} data-color="green">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{workforceOverview.totalActiveStaff.toLocaleString()}</span>
                <span className={styles.metricLabel}>Active Staff</span>
              </div>
              <div className={styles.metricTrend} data-positive={workforceOverview.growthRate >= 0}>
                {workforceOverview.growthRate >= 0 ? '↑' : '↓'} {Math.abs(workforceOverview.growthRate).toFixed(1)}%
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={styles.metricIcon} data-color="gold">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{retirementAnalysis.retiringNext1Year}</span>
                <span className={styles.metricLabel}>Retiring in 12 months</span>
              </div>
              <div className={styles.metricSubtext}>
                {retirementAnalysis.retiringNext5Years} in 5 years
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={styles.metricIcon} data-color="blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{recordsHealth.digitizationPercentage.toFixed(0)}%</span>
                <span className={styles.metricLabel}>Records Digitized</span>
              </div>
              <div className={styles.metricSubtext}>
                {recordsHealth.digitizedFiles.toLocaleString()} of {recordsHealth.totalPFiles.toLocaleString()}
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={styles.metricIcon} data-color="purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{recordsHealth.completenessScore.toFixed(0)}%</span>
                <span className={styles.metricLabel}>Record Completeness</span>
              </div>
              <div className={styles.metricSubtext}>
                {recordsHealth.missingCriticalDocuments} missing critical docs
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className={styles.chartsRow}>
            {/* Gender Distribution */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Gender Distribution</h3>
              <div className={styles.donutChart}>
                <div className={styles.donutVisual}>
                  <svg viewBox="0 0 100 100" className={styles.donutSvg}>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="var(--bg-secondary)"
                      strokeWidth="12"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="var(--ghana-green)"
                      strokeWidth="12"
                      strokeDasharray={`${diversityMetrics.gender.malePercentage * 2.51} 251`}
                      strokeDashoffset="0"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <div className={styles.donutCenter}>
                    <span className={styles.donutValue}>{workforceOverview.totalActiveStaff}</span>
                    <span className={styles.donutLabel}>Total</span>
                  </div>
                </div>
                <div className={styles.donutLegend}>
                  <div className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ backgroundColor: 'var(--ghana-green)' }} />
                    <span>Male: {diversityMetrics.gender.male} ({diversityMetrics.gender.malePercentage.toFixed(1)}%)</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ backgroundColor: 'var(--bg-secondary)' }} />
                    <span>Female: {diversityMetrics.gender.female} ({diversityMetrics.gender.femalePercentage.toFixed(1)}%)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Grade Distribution */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Grade Distribution</h3>
              <div className={styles.barChart}>
                {gradeDistribution.byGrade.slice(0, 6).map((grade) => {
                  const maxCount = Math.max(...gradeDistribution.byGrade.map(g => g.count));
                  const percentage = (grade.count / maxCount) * 100;
                  return (
                    <div key={grade.grade} className={styles.barRow}>
                      <span className={styles.barLabel}>{grade.grade}</span>
                      <div className={styles.barContainer}>
                        <div
                          className={styles.bar}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className={styles.barValue}>{grade.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* MDA Comparison */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Staff by MDA</h3>
              <div className={styles.barChart}>
                {mdaComparison.slice(0, 6).map((mda) => {
                  const maxCount = Math.max(...mdaComparison.map(m => m.totalStaff));
                  const percentage = (mda.totalStaff / maxCount) * 100;
                  return (
                    <div key={mda.mdaCode} className={styles.barRow}>
                      <span className={styles.barLabel}>{mda.mdaCode}</span>
                      <div className={styles.barContainer}>
                        <div
                          className={styles.bar}
                          style={{ width: `${percentage}%`, backgroundColor: 'var(--ghana-gold)' }}
                        />
                      </div>
                      <span className={styles.barValue}>{mda.totalStaff}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Alerts Summary */}
          {alerts.length > 0 && (
            <div className={styles.alertsSummary}>
              <h3 className={styles.alertsTitle}>
                <span className={styles.alertIcon}>⚠️</span>
                Active Alerts ({alerts.length})
              </h3>
              <div className={styles.alertsList}>
                {alerts.slice(0, 3).map((alert, index) => (
                  <div key={index} className={styles.alertItem} data-severity={alert.severity}>
                    <span className={styles.alertMessage}>{alert.title}</span>
                    <span className={styles.alertSeverity}>{alert.severity}</span>
                  </div>
                ))}
                {alerts.length > 3 && (
                  <button className={styles.viewAllAlerts} onClick={() => setActiveSection('alerts')}>
                    View all {alerts.length} alerts →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Workforce Section */}
      {activeSection === 'workforce' && (
        <div className={styles.content}>
          <div className={styles.sectionGrid}>
            {/* Status Breakdown */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Staff Status</h3>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem} data-status="active">
                  <span className={styles.statusValue}>{workforceOverview.totalStaffByStatus.active}</span>
                  <span className={styles.statusLabel}>Active</span>
                </div>
                <div className={styles.statusItem} data-status="leave">
                  <span className={styles.statusValue}>{workforceOverview.totalStaffByStatus.onLeave}</span>
                  <span className={styles.statusLabel}>On Leave</span>
                </div>
                <div className={styles.statusItem} data-status="seconded">
                  <span className={styles.statusValue}>{workforceOverview.totalStaffByStatus.seconded}</span>
                  <span className={styles.statusLabel}>Seconded</span>
                </div>
                <div className={styles.statusItem} data-status="suspended">
                  <span className={styles.statusValue}>{workforceOverview.totalStaffByStatus.suspended}</span>
                  <span className={styles.statusLabel}>Suspended</span>
                </div>
              </div>
            </div>

            {/* Key Statistics */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Workforce Statistics</h3>
              <div className={styles.statsList}>
                <div className={styles.statsRow}>
                  <span className={styles.statsLabel}>Average Age</span>
                  <span className={styles.statsValue}>{workforceOverview.averageAge.toFixed(1)} years</span>
                </div>
                <div className={styles.statsRow}>
                  <span className={styles.statsLabel}>Average Service Years</span>
                  <span className={styles.statsValue}>{workforceOverview.averageServiceYears.toFixed(1)} years</span>
                </div>
                <div className={styles.statsRow}>
                  <span className={styles.statsLabel}>New Appointments (YTD)</span>
                  <span className={styles.statsValue}>{workforceOverview.newAppointmentsThisYear}</span>
                </div>
                <div className={styles.statsRow}>
                  <span className={styles.statsLabel}>Separations (YTD)</span>
                  <span className={styles.statsValue}>{workforceOverview.separationsThisYear}</span>
                </div>
                <div className={styles.statsRow}>
                  <span className={styles.statsLabel}>Turnover Rate</span>
                  <span className={styles.statsValue}>{workforceOverview.staffTurnoverRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* Diversity Metrics */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Diversity Metrics</h3>
              <div className={styles.diversityContent}>
                <div className={styles.genderBar}>
                  <div
                    className={styles.genderSegment}
                    data-gender="male"
                    style={{ width: `${diversityMetrics.gender.malePercentage}%` }}
                  >
                    {diversityMetrics.gender.malePercentage.toFixed(0)}% Male
                  </div>
                  <div
                    className={styles.genderSegment}
                    data-gender="female"
                    style={{ width: `${diversityMetrics.gender.femalePercentage}%` }}
                  >
                    {diversityMetrics.gender.femalePercentage.toFixed(0)}% Female
                  </div>
                </div>
                <div className={styles.diversityStats}>
                  <div className={styles.diversityStat}>
                    <span className={styles.diversityValue}>{diversityMetrics.gender.male}</span>
                    <span className={styles.diversityLabel}>Male Staff</span>
                  </div>
                  <div className={styles.diversityStat}>
                    <span className={styles.diversityValue}>{diversityMetrics.gender.female}</span>
                    <span className={styles.diversityLabel}>Female Staff</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Retirement Section */}
      {activeSection === 'retirement' && (
        <div className={styles.content}>
          {/* Retirement Summary Cards */}
          <div className={styles.retirementCards}>
            <div className={styles.retirementCard} data-urgency="immediate">
              <span className={styles.retirementNumber}>{retirementAnalysis.retiringThisYear}</span>
              <span className={styles.retirementPeriod}>This Year</span>
            </div>
            <div className={styles.retirementCard} data-urgency="soon">
              <span className={styles.retirementNumber}>{retirementAnalysis.retiringNext1Year}</span>
              <span className={styles.retirementPeriod}>Next 12 Months</span>
            </div>
            <div className={styles.retirementCard} data-urgency="medium">
              <span className={styles.retirementNumber}>{retirementAnalysis.retiringNext5Years}</span>
              <span className={styles.retirementPeriod}>Next 5 Years</span>
            </div>
            <div className={styles.retirementCard} data-urgency="long">
              <span className={styles.retirementNumber}>{retirementAnalysis.retiringNext10Years}</span>
              <span className={styles.retirementPeriod}>Next 10 Years</span>
            </div>
          </div>

          {/* Retirement by Grade */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Retirements by Grade</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>1 Year</th>
                    <th>5 Years</th>
                    <th>10 Years</th>
                  </tr>
                </thead>
                <tbody>
                  {retirementAnalysis.retirementsByGrade.map((item) => (
                    <tr key={item.grade}>
                      <td>{item.grade}</td>
                      <td>{item.count1Year}</td>
                      <td>{item.count5Years}</td>
                      <td>{item.count10Years}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Succession Risk */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Succession Planning</h3>
            <div className={styles.successionGrid}>
              <div className={styles.successionItem}>
                <span className={styles.successionValue}>{retirementAnalysis.successionRisk.criticalPositions}</span>
                <span className={styles.successionLabel}>Critical Positions</span>
              </div>
              <div className={styles.successionItem} data-status="good">
                <span className={styles.successionValue}>{retirementAnalysis.successionRisk.withSuccessor}</span>
                <span className={styles.successionLabel}>With Successor</span>
              </div>
              <div className={styles.successionItem} data-status="risk">
                <span className={styles.successionValue}>{retirementAnalysis.successionRisk.atRisk}</span>
                <span className={styles.successionLabel}>At Risk</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Records Health Section */}
      {activeSection === 'records' && (
        <div className={styles.content}>
          {/* Records Overview */}
          <div className={styles.recordsOverview}>
            <div className={styles.recordsStat}>
              <span className={styles.recordsValue}>{recordsHealth.totalPFiles.toLocaleString()}</span>
              <span className={styles.recordsLabel}>Total P-Files</span>
            </div>
            <div className={styles.recordsStat}>
              <span className={styles.recordsValue}>{recordsHealth.digitizedFiles.toLocaleString()}</span>
              <span className={styles.recordsLabel}>Digitized</span>
            </div>
            <div className={styles.recordsStat}>
              <span className={styles.recordsValue}>{recordsHealth.averageDocumentsPerFile.toFixed(1)}</span>
              <span className={styles.recordsLabel}>Avg Docs/File</span>
            </div>
            <div className={styles.recordsStat}>
              <span className={styles.recordsValue}>{recordsHealth.recordsNeedingUpdate}</span>
              <span className={styles.recordsLabel}>Need Update</span>
            </div>
          </div>

          {/* Completeness Breakdown */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Completeness Breakdown</h3>
            <div className={styles.completenessGrid}>
              <div className={styles.completenessItem} data-level="excellent">
                <span className={styles.completenessValue}>{recordsHealth.completenessBreakdown.excellent}</span>
                <span className={styles.completenessLabel}>Excellent (90-100%)</span>
              </div>
              <div className={styles.completenessItem} data-level="good">
                <span className={styles.completenessValue}>{recordsHealth.completenessBreakdown.good}</span>
                <span className={styles.completenessLabel}>Good (70-89%)</span>
              </div>
              <div className={styles.completenessItem} data-level="fair">
                <span className={styles.completenessValue}>{recordsHealth.completenessBreakdown.fair}</span>
                <span className={styles.completenessLabel}>Fair (50-69%)</span>
              </div>
              <div className={styles.completenessItem} data-level="poor">
                <span className={styles.completenessValue}>{recordsHealth.completenessBreakdown.poor}</span>
                <span className={styles.completenessLabel}>Poor (&lt;50%)</span>
              </div>
            </div>
          </div>

          {/* Digitization Progress */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Digitization Progress</h3>
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${recordsHealth.digitizationPercentage}%` }}
                />
              </div>
              <div className={styles.progressStats}>
                <span>{recordsHealth.digitizationPercentage.toFixed(1)}% Complete</span>
                <span>{recordsHealth.totalPFiles - recordsHealth.digitizedFiles} remaining</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Section */}
      {activeSection === 'alerts' && (
        <div className={styles.content}>
          <div className={styles.alertsContainer}>
            {alerts.length === 0 ? (
              <div className={styles.noAlerts}>
                <span className={styles.noAlertsIcon}>✓</span>
                <span>No active alerts</span>
              </div>
            ) : (
              alerts.map((alert, index) => (
                <div key={index} className={styles.alertCard} data-severity={alert.severity}>
                  <div className={styles.alertHeader}>
                    <span className={styles.alertType}>{executiveDashboard.getAlertTypeLabel(alert.type)}</span>
                    <span className={styles.alertSeverityBadge}>{alert.severity}</span>
                  </div>
                  <p className={styles.alertDescription}>{alert.description}</p>
                  {alert.affectedMDAs && alert.affectedMDAs.length > 0 && (
                    <span className={styles.alertAffected}>{alert.affectedMDAs.length} MDAs affected</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
